/**
 * WorkspaceDropdown - compact "Drafting for" trigger that mounts inside the
 * objectives-step chat composer's action row.
 *
 * The wizard previously surfaced workspace context via a wide "DRAFTING FOR …"
 * banner above the composer. That banner has been folded into this dropdown
 * so the workspace switch lives _on_ the chat modal itself: a small chip
 * showing the active workspace's marker + name, expanding to a list the user
 * can click to switch. A footer "+ New workspace" link opens the same
 * `CreateWorkspaceModal` the sidebar tree uses, gated on the
 * `brands:create` permission.
 *
 * Styling intentionally mirrors the existing left-rail switcher chip
 * (`WorkspaceSwitcher`) so the two surfaces feel like the same affordance -
 * just sized down to fit inside the composer rail.
 *
 * @module components/workspace/WorkspaceDropdown
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconSelector, IconCheck } from '@tabler/icons-react';
import { PlusIcon } from '@/components/icons';
import { clsx } from 'clsx';
import { useOrg } from '../../lib/OrgProvider';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';
import { WorkspaceMarker } from './WorkspaceMarker';

/** Viewport-anchored coordinates used to position the open menu. */
interface MenuRect {
  /** Distance from the viewport's left edge to the menu's left edge, in px. */
  left: number;
  /** Distance from the viewport's bottom edge to the menu's bottom edge, in px. */
  bottom: number;
}

/**
 * Compact workspace dropdown for the objectives chat composer. Renders
 * nothing until an active workspace is loaded so the composer doesn't show a
 * skeleton chip during the first render.
 *
 * The popover renders into a `document.body` portal because the chat composer
 * wrapper combines `glass-card` (which sets `backdrop-filter: blur(20px)`,
 * promoting it to a containing block for `position: fixed`) with
 * `overflow-hidden`. Without the portal, even a fixed-positioned menu would
 * be clipped by the composer card. Coordinates are recomputed on open and
 * on viewport scroll/resize while the menu is showing.
 */
export const WorkspaceDropdown: React.FC = () => {
  const { workspaces, activeWorkspace, setActiveWorkspaceId, hasPermission } =
    useOrg();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<MenuRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const canCreateWorkspace = hasPermission('brands:create');

  /**
   * Read the trigger's viewport rect and translate it into viewport-anchored
   * coordinates that place the menu's bottom-left 8px above the trigger's
   * top-left. Used both on initial open and when the viewport changes while
   * the menu is showing.
   */
  const recomputePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuRect({
      left: rect.left,
      bottom: window.innerHeight - rect.top + 8,
    });
  }, []);

  // Close on outside click so the dropdown behaves like a normal popover and
  // doesn't trap focus when the user clicks back into the textarea. Because
  // the menu is portalled, we must allow clicks inside it _and_ inside the
  // trigger before treating the click as "outside".
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Anchor the portalled menu to the trigger. `useLayoutEffect` reads the
  // rect synchronously after the menu mounts so there's no first-frame
  // flicker at the wrong coordinates.
  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return undefined;
    }
    recomputePosition();
    const onChange = () => recomputePosition();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [open, recomputePosition]);

  if (!activeWorkspace) return null;

  const menu = open ? (
    <div
      ref={menuRef}
      role="menu"
      style={
        menuRect
          ? { position: 'fixed', left: menuRect.left, bottom: menuRect.bottom }
          : { position: 'fixed', visibility: 'hidden' }
      }
      className="min-w-[220px] max-w-[260px] bg-background border border-border rounded-xl z-50 py-1 max-h-72 overflow-y-auto"
    >
      {workspaces.map((w) => {
        const isActive = w.id === activeWorkspace.id;
        return (
          <button
            key={w.id}
            type="button"
            role="menuitem"
            onClick={() => {
              if (!isActive) setActiveWorkspaceId(w.id);
              setOpen(false);
            }}
            aria-label={w.name}
            className={clsx(
              'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left',
              isActive
                ? 'bg-volt/10 text-volt-soft'
                : 'text-muted-foreground hover:bg-surface-hover',
            )}
          >
            <WorkspaceMarker workspace={w} size={16} />
            <span className="flex-1 truncate">{w.name}</span>
            {isActive && (
              <IconCheck
                className="w-4 h-4 text-volt shrink-0"
                data-testid={`workspace-dropdown-active-${w.id}`}
              />
            )}
          </button>
        );
      })}
      {canCreateWorkspace && (
        <button
          type="button"
          role="menuitem"
          aria-label="New workspace"
          onClick={() => {
            setOpen(false);
            setCreateOpen(true);
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-surface-hover border-t border-border transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          <span>New workspace</span>
        </button>
      )}
    </div>
  ) : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="workspace-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Switch workspace"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Drafting for ${activeWorkspace.name}`}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors whitespace-nowrap"
      >
        <WorkspaceMarker
          workspace={activeWorkspace}
          size={18}
          testId="workspace-dropdown-marker"
        />
        <span className="font-medium">{activeWorkspace.name}</span>
        <IconSelector className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      </button>

      {menu && typeof document !== 'undefined'
        ? createPortal(menu, document.body)
        : null}

      <CreateWorkspaceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
};

export default WorkspaceDropdown;
